import type { CreatorSummary, OutreachCard, ProspectRunDetail } from '../../../services/dataService';

/**
 * SCOUT MY CHANNEL — the shapes and the plain rules behind the scout view (?scout=), lifted out of
 * the old Brand Scout page (/app/prospects, deleted) with no change to what they mean. Everything a
 * shortlist shows comes off the server's result: its brands, their calculated fit, their reasons
 * (only those that passed the source check), their sources, the brands Bearri compared. No number
 * here is invented, and a brand is never matched to a deal by its name.
 */

/** A public sponsor mention from another creator — the source a reason is checked against. */
export interface ScoutSource {
  creator_name?: string;
  creator_handle?: string;
  title?: string;
  url: string;
  observed_at?: string | null;
}

/** One ranked brand, as the scout engine returns it (the subset the file prints). */
export interface ScoutRec {
  brand: string;
  brand_id?: string;
  category: string;
  score: number;
  fit_bucket?: string;
  /** What the brand sells (only when it passed the source check). */
  description?: string;
  reasoning: string;
  pitch_angle: string;
  evidence?: ScoutSource[];
  /** reviewed = its prose passed the source check · evidence_only = the prose was blanked. */
  explanation_status?: 'reviewed' | 'evidence_only';
  logo_url?: string | null;
  brand_color?: string | null;
  /** Brand site on file — the contact finder uses the real domain instead of guessing one. */
  website?: string;
  sponsorship_count?: number;
  creator_count?: number;
  shared_with_creators?: string[];
  /** The creator's OWN pitch history with this brand, stamped by the server on a live result. */
  pitch_history?: { status: string; last_at: string | null } | null;
}

/** channel = a scout of your own channel · similar = brands like one on your channel · self = an old self-described run. */
export type ShortlistKind = 'channel' | 'similar' | 'self';

/** A filed shortlist: a live scout's result or a saved run reopened. */
export interface Shortlist {
  /** The saved run; null only for a scout that filed nothing (it is not saved). */
  runId: string | null;
  kind: ShortlistKind;
  /** The channel it was scouted for. */
  creator: CreatorSummary | null;
  /** similar: the brand it started from · self: the niche it was described as. */
  anchor: string | null;
  /** Ranked best first. */
  recs: ScoutRec[];
  /** "Bearri compared 40 of the 989 brands it tracks" — only when the engine reported both. */
  considered: number | null;
  tracked: number | null;
  filedAt: string | null;
  /** Opened from the saved list: free, and nothing was spent now. */
  reopened: boolean;
  /** A live scout: whether it spent a scout (the server charges only when a reason passed the source check). */
  charged: boolean | null;
}

/** What a "Scout 10 brands" press runs. The server checks the channel is yours (siblings allowed). */
export type ScoutTarget =
  | { mode: 'channel'; platform: string; handle: string }
  | { mode: 'similar'; brand: string; platform: string; handle: string };

/** The fixed size of a scout, as Brand Scout ran it. */
export const SCOUT_SIZE = 10;

/** The job the press starts — exactly the inputs Brand Scout sent. */
export function scoutJob(target: ScoutTarget): { kind: 'scout' | 'similar_scout'; input: Record<string, unknown> } {
  return target.mode === 'channel'
    ? { kind: 'scout', input: { platform: target.platform, handle: target.handle, max_results: SCOUT_SIZE } }
    : { kind: 'similar_scout', input: { source_brand: target.brand, my_creator_handle: target.handle, my_platform: target.platform, count: SCOUT_SIZE } };
}

// ── fit: a calculated score (category + size), never written by the model ──
export type BucketKey = 'strong' | 'worth_a_shot' | 'stretch';
export const BUCKETS: Record<BucketKey, { label: string; plural: string; order: number }> = {
  strong: { label: 'Strong fit', plural: 'strong', order: 0 },
  worth_a_shot: { label: 'Worth a shot', plural: 'worth a shot', order: 1 },
  stretch: { label: 'Stretch', plural: 'stretch', order: 2 },
};
export function bucketOf(r: ScoutRec): BucketKey {
  if (r.fit_bucket && r.fit_bucket in BUCKETS) return r.fit_bucket as BucketKey;
  return r.score >= 70 ? 'strong' : r.score >= 48 ? 'worth_a_shot' : 'stretch';
}

/** Best first: by fit, then score. A brand that already said no sinks to the bottom (as the server sorts it). */
export function rank(recs: ScoutRec[]): ScoutRec[] {
  const lost = (r: ScoutRec) => (r.pitch_history?.status === 'lost' ? 1 : 0);
  // a row whose reason held up against a real mention comes before one whose reason didn't: never lead with a guess
  const unchecked = (r: ScoutRec) => (isBlanked(r) ? 1 : 0);
  return [...recs].sort((a, b) => lost(a) - lost(b) || unchecked(a) - unchecked(b)
    || BUCKETS[bucketOf(a)].order - BUCKETS[bucketOf(b)].order || b.score - a.score);
}

export function fitCounts(recs: ScoutRec[]): Record<BucketKey, number> {
  const out: Record<BucketKey, number> = { strong: 0, worth_a_shot: 0, stretch: 0 };
  recs.forEach(r => { out[bucketOf(r)] += 1; });
  return out;
}

/** Its written reason was blanked: it did not pass the source check (or it was saved before there was one). */
export const isBlanked = (r: ScoutRec) => r.explanation_status === 'evidence_only';
/** Blanked, and no source was kept either — an old saved suggestion. */
export const isBare = (r: ScoutRec) => isBlanked(r) && !(r.evidence && r.evidence.length);

export const BARE_COPY = 'Saved before Bearri checked sources, so no reasons or sources were kept. Run Find brands again for checked reasons.';
export const SOURCES_ONLY_COPY = 'Its reason didn’t pass Bearri’s source check, so only the sources were kept. Read them before you choose an angle.';

/** The creator's own history with a brand (server-stamped, exact brand key) — kept so a brand that said no isn't pitched as new. */
export function historyLine(r: ScoutRec): string | null {
  const h = r.pitch_history;
  if (!h) return null;
  const when = sinceLabel(h.last_at);
  switch (h.status) {
    case 'won': return `You won this brand${when ? ` ${when}` : ''}`;
    case 'lost': return `Didn’t land when you pitched${when ? ` ${when}` : ''} — a fresh angle is needed`;
    case 'sent': return `You already pitched them${when ? ` ${when}` : ''}`;
    case 'drafted': return `You have a draft for them${when ? ` from ${when}` : ''}`;
    default: return null;
  }
}

/** Real engine fields only — never a fabricated "your size" figure or a dollar amount. */
export function signalsFor(r: ScoutRec): string[] {
  const out: string[] = [];
  if (typeof r.creator_count === 'number' && r.creator_count > 0) {
    out.push(`Detected on ${r.creator_count} creator${r.creator_count === 1 ? '' : 's'} in our data`);
  }
  if (r.shared_with_creators && r.shared_with_creators.length) {
    out.push(`Also sponsors ${r.shared_with_creators.slice(0, 2).map(h => `@${h}`).join(', ')}`);
  }
  return out;
}

/** "https://www.nordvpn.com/deal" → "nordvpn.com", for the contact finder. */
export function domainFromWebsite(website?: string): string | undefined {
  if (!website) return undefined;
  try {
    const host = new URL(website.includes('://') ? website : `https://${website}`).hostname.replace(/^www\./i, '');
    return host.includes('.') ? host : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The deal a shortlisted brand already has — ONLY through the relation the deal stored when it was
 * drafted from a shortlist (Pitch Writer saves research_context {run_id, brand}). Never a name
 * lookup: a deal the creator started some other way is not claimed for this row.
 */
export function dealFor(r: ScoutRec, cards: OutreachCard[], runId: string | null): OutreachCard | null {
  const drafted = cards.filter(c => {
    const research = (c as OutreachCard & { research_context?: { run_id?: string; brand?: string } | null }).research_context;
    return !!research && research.brand === r.brand && !!research.run_id;
  });
  const own = runId ? drafted.find(c => (c as OutreachCard & { research_context?: { run_id?: string } }).research_context?.run_id === runId) : undefined;
  return own || drafted[0] || null;
}

/** Pitch Writer, with this shortlist attached — exactly the hand-off Brand Scout made. */
export function pitchWriterPath(list: Shortlist, brand: string, fallback: { platform: string; handle: string } | null): string {
  const params = new URLSearchParams();
  // the pitch is for the channel the shortlist was scouted for (the server checks they match)
  const who = list.creator?.handle && list.creator.platform ? list.creator : fallback;
  if (who) { params.set('creator_platform', who.platform); params.set('creator_handle', who.handle); }
  params.set('brand', brand);
  if (list.runId) params.set('scout_run', list.runId);
  return `/app/pitch-writer?${params.toString()}`;
}

/** The first-person question "Ask Bearri about <brand>" leaves on a new conversation's pad. Nothing runs. */
export const askAbout = (brand: string) => `Help me pitch ${brand}. What should my first email to them lead with?`;

// ── reading a result ─────────────────────────────────────────────────────────
const recsOf = (value: unknown): ScoutRec[] => (Array.isArray(value) ? value.filter(r => r && typeof r === 'object' && typeof (r as ScoutRec).brand === 'string') as ScoutRec[] : []);
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
function metaOf(output: Record<string, unknown>) {
  const meta = (output.meta && typeof output.meta === 'object' ? output.meta : {}) as Record<string, unknown>;
  return { considered: num(meta.candidates_considered), tracked: num(meta.total_brands_in_db) };
}

/** A scout that just finished (the job's result). */
export function shortlistFromResult(result: Record<string, unknown>, target: { kind: 'scout' | 'similar_scout'; anchor?: string | null },
  now = new Date().toISOString()): Shortlist {
  const anchor = result.anchor_brand && typeof result.anchor_brand === 'object' ? String((result.anchor_brand as { name?: string }).name || '') : '';
  return {
    runId: typeof result.run_id === 'string' ? result.run_id : null,
    kind: target.kind === 'similar_scout' ? 'similar' : 'channel',
    creator: (result.creator as CreatorSummary | null) || null,
    anchor: anchor || target.anchor || null,
    recs: rank(recsOf(result.recommendations)),
    ...metaOf(result),
    filedAt: now,
    reopened: false,
    charged: typeof result.usage_charged === 'boolean' ? result.usage_charged : null,
  };
}

/** A saved run, reopened (free). */
export function shortlistFromRun(run: ProspectRunDetail): Shortlist {
  const output = (run.output_payload || {}) as Record<string, unknown>;
  const input = (run.input_payload || {}) as Record<string, unknown>;
  const kind: ShortlistKind = run.mode === 'similar_to_brand' ? 'similar' : run.mode === 'my_creator' ? 'self' : 'channel';
  const anchorBrand = output.anchor_brand && typeof output.anchor_brand === 'object' ? String((output.anchor_brand as { name?: string }).name || '') : '';
  const profile = (input.profile && typeof input.profile === 'object' ? input.profile : {}) as Record<string, unknown>;
  const anchor = kind === 'similar' ? anchorBrand || (typeof input.source_brand === 'string' ? input.source_brand : '')
    : kind === 'self' ? (typeof profile.niche === 'string' ? profile.niche : '') : '';
  return {
    runId: run.id,
    kind,
    creator: (output.creator as CreatorSummary | null) || null,
    anchor: anchor || null,
    recs: rank(recsOf(output.recommendations)),
    ...metaOf(output),
    filedAt: run.created_at,
    reopened: true,
    charged: null,
  };
}

/** A shortlist's name, worded the way the chats rail names a saved scout (HistoryRail's railShortlistTitle), so its
 *  head and its rail row read the same: "Brands for @novavale" · "Brands like Raycon" · "Self-described: Tech reviews". */
export function shortlistTitle(list: Pick<Shortlist, 'kind' | 'creator' | 'anchor'>): string {
  if (list.kind === 'similar') return list.anchor ? `Brands like ${list.anchor}` : 'Brands like one you named';
  if (list.kind === 'self') return list.anchor ? `Self-described: ${list.anchor}` : 'Self-described channel';
  return list.creator?.handle ? `Brands for @${list.creator.handle.replace(/^@/, '')}` : 'Brands for your channel';
}

/** A running scout's name, the same way: "Brands for @novavale" · "Brands like Raycon". */
export function scoutJobTitle(job: { kind: 'scout' | 'similar_scout'; subject: string }): string {
  const subject = job.subject.trim();
  return job.kind === 'similar_scout'
    ? shortlistTitle({ kind: 'similar', creator: null, anchor: subject || null })
    : subject ? `Brands for @${subject.replace(/^@/, '')}` : 'Brands for your channel';
}

export const PLATFORM_LABEL: Record<string, string> = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok', twitch: 'Twitch' };
export function platformLabel(p?: string | null): string {
  if (!p) return '';
  return PLATFORM_LABEL[p.toLowerCase()] || p.charAt(0).toUpperCase() + p.slice(1);
}

/** "YouTube @novavale" */
export const channelLabel = (c: { platform: string; handle: string }) => `${platformLabel(c.platform)} @${c.handle}`;

/** The channel's figures as one line of ink, from its saved detail — only what can be sourced. */
export function channelFigures(detail: { creator: CreatorSummary; shop?: unknown[] }): string[] {
  const c = detail.creator;
  const lines: string[] = [];
  const reach = c.subscriber_count ?? c.followers_count;
  if (typeof reach === 'number' && reach > 0) lines.push(`${compact(reach)} ${c.platform === 'youtube' ? 'subscribers' : 'followers'}`);
  if (typeof c.view_count === 'number' && typeof c.video_count === 'number' && c.video_count > 0) {
    lines.push(`Avg ${compact(Math.round(c.view_count / c.video_count))} views / video`);
  }
  if (typeof c.sponsorship_count === 'number' && c.sponsorship_count > 0) lines.push(`${c.sponsorship_count} sponsor${c.sponsorship_count === 1 ? '' : 's'} detected`);
  if (detail.shop && detail.shop.length) lines.push('Merch shop live');
  if (typeof c.code_count === 'number' && c.code_count > 0) lines.push(`${c.code_count} code${c.code_count === 1 ? '' : 's'} tracked`);
  return lines;
}

export function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return String(n);
}

/** "today" · "yesterday" · "12 days ago" — whole days from the server's timestamp. */
export function sinceLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const d = Math.floor((Date.now() - t) / 86_400_000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
}

/** "10:12" — the time a job was accepted, in the creator's own clock. */
export function clockLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** An old /app/prospects link, kept forever (emails, notifications, onboarding): ?run=X → the shortlist, anything else → the order. */
export function scoutPathFor(search: string): string {
  const run = new URLSearchParams(search).get('run');
  return `/app/bearri?scout=${encodeURIComponent(run || 'new')}`;
}

const PLATFORM_NAMES: Record<string, string> = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok', twitch: 'Twitch' };

/** A brand-search chat's name, the same in the rail and the chat head: "Brands for my YouTube" (a self-described run
 *  has no channel: "Brands for my channel", never a guessed platform). */
export function shortlistName(scout: { platform: string | null }): string {
  return `Brands for my ${scout.platform ? PLATFORM_NAMES[scout.platform.toLowerCase()] || scout.platform : 'channel'}`;
}
